// ─── Onboarding (F2) — types ────────────────────────────────────────────────

// De DTO's hieronder typen `status` met de volle 10-waarde C-flow-union
// (`OnboardingStatusValue`), niet meer met de legacy 4-waarde `OnboardingStatus`
// — de API serialiseert de extended set. Zie `./onboarding-dashboard.ts` voor
// de SSoT van die union + labels + badge-varianten.
import type { OnboardingStatusValue } from './onboarding-dashboard'
// batch-42 onboarding (FO-369) — afgeleide "wacht op"-reden per onboarding.
import type { OnboardingBlokkadeDTO } from './onboarding-blokkade'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const ONBOARDING_STATUS = [
  'CONCEPT',
  'IN_BEHANDELING',
  'AFGEROND',
  'GEANNULEERD',
] as const
export type OnboardingStatus = (typeof ONBOARDING_STATUS)[number]

export const ONBOARDING_STAP_TYPE = [
  'PERSOONLIJK',
  'CONTRACT',
  'CERTIFICATEN',
  'DOCUMENTEN',
  'IT_TOEGANG',
  'WERKKLEDING',
  'AKKOORD',
] as const
export type OnboardingStapType = (typeof ONBOARDING_STAP_TYPE)[number]

export const ONBOARDING_STAP_STATUS = [
  'TODO',
  'BEZIG',
  // Spec 25 §4 — kandidaat heeft ingediend; wacht op HR-controle.
  // Cursor skipt 'm; HR-controle zet 'm naar AFGEROND/AFGEWEZEN.
  'INGEDIEND',
  'AFGEROND',
  'OVERGESLAGEN',
  // F3.3 — HR-afgewezen stap; medewerker moet opnieuw indienen.
  'AFGEWEZEN',
] as const
export type OnboardingStapStatus = (typeof ONBOARDING_STAP_STATUS)[number]

// F3.2 — categorieën (één stap per categorie in het standaard-template).
// C0 — KEUZE toegevoegd zodat de TS-union 1:1 aansluit op de Prisma-enum.
export const ONBOARDING_CATEGORIE = [
  'PERSOONLIJK',
  'COMPLIANCE',
  'VAKBEKWAAMHEID',
  'JURIDISCH',
  'UITRUSTING',
  'MOBILITEIT',
  'SYSTEMEN',
  'VEILIGHEID',
  'KEUZE',
  // batch-39 onboarding (FO-337) — samengevoegde stap "Reglementen & akkoorden".
  'REGLEMENTEN',
] as const
export type OnboardingCategorie = (typeof ONBOARDING_CATEGORIE)[number]

export const ONBOARDING_CATEGORIE_LABELS: Record<OnboardingCategorie, string> = {
  PERSOONLIJK:    'Persoonlijke gegevens',
  COMPLIANCE:     'Identiteit & compliance',
  VAKBEKWAAMHEID: 'Vakbekwaamheid',
  JURIDISCH:      'Contract & juridisch',
  UITRUSTING:     'Kleding & uitrusting',
  MOBILITEIT:     'Mobiliteit',
  SYSTEMEN:       'ICT & toegang',
  VEILIGHEID:     'Veiligheid & akkoorden',
  KEUZE:          'Keuze',
  REGLEMENTEN:    'Reglementen & akkoorden',
}

// F3.2 — wie vult de stap in.
export const WIE_VULT_STAP = ['HR', 'MEDEWERKER'] as const
export type WieVultStap = (typeof WIE_VULT_STAP)[number]

export const WIE_VULT_STAP_LABELS: Record<WieVultStap, string> = {
  HR:         'HR vult vooraf in',
  MEDEWERKER: 'Medewerker vult zelf in',
}

// `actie` op OnboardingAudit is een vrije string (Prisma-veld), maar we typen
// 'm aan de TS-kant met een union zodat code-paths consistent blijven.
export const ONBOARDING_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'STAP_VOLTOOID',
  'STAP_OVERGESLAGEN',
  'STAP_HEROPEND',
  'STATUS_GEWIJZIGD',
  'AFGEROND',
  'GEANNULEERD',
  // batch-45 onboarding (FO-381) — ADMIN-verwijdering (soft) van de registratie.
  'VERWIJDERD',
  'NOTITIE_TOEGEVOEGD',
  // F3.3 — per-stap goedkeur/afwijs.
  'STAP_GOEDGEKEURD',
  'STAP_AFGEWEZEN',
] as const
export type OnboardingAuditActie = (typeof ONBOARDING_AUDIT_ACTIE)[number]

// ─── Volgorde — leidend voor wizard-rendering ────────────────────────────────

/**
 * Standaard-volgorde van de 7 stappen. Wordt zowel gebruikt bij het seeden
 * van stappen (POST /onboarding) als bij de UI-sortering. Werd intern op een
 * volgorde-veld opgeslagen, maar deze constante is de SSoT.
 */
export const ONBOARDING_STAP_VOLGORDE: Record<OnboardingStapType, number> = {
  PERSOONLIJK:  1,
  CONTRACT:     2,
  CERTIFICATEN: 3,
  DOCUMENTEN:   4,
  IT_TOEGANG:   5,
  WERKKLEDING:  6,
  AKKOORD:      7,
}

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const ONBOARDING_STATUS_LABELS: Record<OnboardingStatus, string> = {
  CONCEPT:        'Concept',
  IN_BEHANDELING: 'In behandeling',
  AFGEROND:       'Afgerond',
  GEANNULEERD:    'Geannuleerd',
}

export const ONBOARDING_STAP_TYPE_LABELS: Record<OnboardingStapType, string> = {
  PERSOONLIJK:  'Persoonlijk',
  CONTRACT:     'Contract',
  CERTIFICATEN: 'Certificaten',
  DOCUMENTEN:   'Documenten',
  IT_TOEGANG:   'IT-toegang',
  WERKKLEDING:  'Werkkleding',
  AKKOORD:      'Akkoord',
}

export const ONBOARDING_STAP_TYPE_BESCHRIJVING: Record<OnboardingStapType, string> = {
  PERSOONLIJK:  'Basisgegevens, BSN, foto, adres',
  CONTRACT:     'Contracttype, uren, salaris, startdatum',
  CERTIFICATEN: 'Verplichte certificaten controleren',
  DOCUMENTEN:   'ID-kopie, handtekening op contract',
  IT_TOEGANG:   'Entra-account, e-mail, MFA',
  WERKKLEDING:  'Kledingmaten, gereedschapset',
  AKKOORD:      'HR-akkoord en medewerker-handtekening',
}

export const ONBOARDING_STAP_STATUS_LABELS: Record<OnboardingStapStatus, string> = {
  TODO:         'Te doen',
  BEZIG:        'Bezig',
  INGEDIEND:    'Ingediend',
  AFGEROND:     'Goedgekeurd',
  OVERGESLAGEN: 'Overgeslagen',
  AFGEWEZEN:    'Afgewezen',
}

export const ONBOARDING_AUDIT_ACTIE_LABELS: Record<OnboardingAuditActie, string> = {
  AANGEMAAKT:         'Aangemaakt',
  BIJGEWERKT:         'Bijgewerkt',
  STAP_VOLTOOID:      'Stap voltooid',
  STAP_OVERGESLAGEN:  'Stap overgeslagen',
  STAP_HEROPEND:      'Stap heropend',
  STATUS_GEWIJZIGD:   'Status gewijzigd',
  AFGEROND:           'Onboarding afgerond',
  GEANNULEERD:        'Onboarding geannuleerd',
  VERWIJDERD:         'Onboarding verwijderd',
  NOTITIE_TOEGEVOEGD: 'Notitie toegevoegd',
  STAP_GOEDGEKEURD:   'Stap goedgekeurd',
  STAP_AFGEWEZEN:     'Stap afgewezen',
}

// Status badge variant voor StatusBadge-component.
export const ONBOARDING_STATUS_BADGE_VARIANT: Record<
  OnboardingStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT:        'secondary', // grijs — nog te starten
  IN_BEHANDELING: 'warning',   // oranje — loopt
  AFGEROND:       'success',   // groen — klaar
  GEANNULEERD:    'danger',    // rood — gestopt
}

export const ONBOARDING_STAP_STATUS_BADGE_VARIANT: Record<
  OnboardingStapStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  TODO:         'secondary',
  BEZIG:        'warning',
  INGEDIEND:    'info',
  AFGEROND:     'success',
  OVERGESLAGEN: 'info',
  AFGEWEZEN:    'danger',
}

// ─── DTOs voor API responses ─────────────────────────────────────────────────

export interface OnboardingStapDTO {
  id: string
  type: OnboardingStapType
  status: OnboardingStapStatus
  volgorde: number
  notitie: string | null
  voltooidDoor: string | null
  voltooidDoorNaam: string | null // computed via User-lookup
  voltooidOp: string | null
  // F3.2 — template-koppeling + nieuwe velden. Nullable zodat oude rijen
  // (van vóór F3.2) blijven werken.
  categorie: OnboardingCategorie | null
  prioriteit: string | null
  formulierData: unknown | null
  // Spec 25 §5.6 — schema meesturen zodat de backoffice Stappen-tab de
  // ingevulde data via labels (i.p.v. raw veld-keys) kan renderen. Bevat
  // `FormulierVeldDefinitie[]` JSON van de gekoppelde TemplateStap, of
  // null voor legacy-rijen zonder templateStapId.
  formulierSchema: unknown | null
  templateStapId: string | null
  // F3.3 — bij AFGEWEZEN status bevat dit veld de reden van HR.
  afwijzingsReden: string | null
  // batch-39 (FO-337) — wie vult de stap (uit de template-stap; null voor
  // legacy-rijen). De backoffice toont hiermee wie er aan zet is.
  wieVult: WieVultStap | null
  // batch-39 (FO-337 §2.4) — true wanneer de stap actief maar niet invulbaar
  // is (template-koppeling kwijt in een template-gedreven onboarding): de
  // wizard kan hem niet tonen; HR moet de configuratie herstellen.
  nietInvulbaar: boolean
  // batch-39 (FO-337) — true wanneer de stap voor deze medewerker verborgen
  // is en nergens meetelt (bv. reglementen-stap zonder actieve reglementen).
  verborgen: boolean
  createdAt: string
  updatedAt: string
}

export interface OnboardingAuditDTO {
  id: string
  actie: string // OnboardingAuditActie of vrije string voor forward-compat
  actor: string
  actorName: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

export interface OnboardingListRowDTO {
  id: string
  employeeId: string
  employeeName: string
  employeeNumber: string | null
  functionTitle: string | null
  status: OnboardingStatusValue
  startDatum: string | null
  daadwerkelijkeStart: string | null
  ingediendOp: string
  voltooidOp: string | null
  // computed
  aantalStappenAfgerond: number
  aantalStappenTotaal: number
  doorlooptijdDagen: number | null // alleen gevuld bij AFGEROND
  // batch-42 onboarding (FO-369) — afgeleide "wacht op"-reden (lijst-kolom).
  blokkade: OnboardingBlokkadeDTO
}

export interface OnboardingDetailDTO {
  id: string
  employeeId: string
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeNumber: string | null
    functionTitle: string | null
    status: string
    /** Privé-mailadres — bestemming voor de welkom-link mail. */
    privateEmail: string | null
    /** Werk-mailadres (Microsoft-login) — fallback bestemming. */
    workEmail: string | null
    /**
     * Spec 25 §5.1 — status van de contract-velden die nodig zijn voor
     * `generateOnboardingDocument({type:'ARBEIDSOVEREENKOMST'})`. HR vult
     * deze in op /medewerkers/[id]; de backoffice toont een banner wanneer
     * ze ontbreken bij een IN_CONTROLE-onboarding.
     */
    contractFieldsComplete: boolean
    /** Lijst van ontbrekende veld-keys (leeg als alle vereisten gevuld zijn). */
    missingContractFields: string[]
  }
  status: OnboardingStatusValue
  startDatum: string | null
  daadwerkelijkeStart: string | null
  ingediendDoor: string
  ingediendDoorNaam: string | null
  ingediendOp: string
  voltooidOp: string | null
  /**
   * I.3 — aanvullen-ronde als sub-state op IN_CONTROLE. Staat dit aan, dan
   * houdt een /goedkeuren van de laatste stap de contract-generatie tegen
   * (onboarding blijft op IN_CONTROLE) tot de reviewer de vlag klaart via
   * `POST /onboarding/:id/aanvullen/gereed`.
   */
  aanvullenVereist: boolean
  /** Wat de reviewer nog aangevuld wil zien; `null` als er niets openstaat. */
  aanvullenNotitie: string | null
  stappen: OnboardingStapDTO[]
  audits: OnboardingAuditDTO[]
  /**
   * batch-39 (FO-337 §2.4) — medewerker-voortgang uit dezelfde éénbron-
   * afleiding als de wizard: alleen de zichtbare medewerker-stappen tellen.
   * De backoffice toont deze balk naast (niet gemengd met) het HR-werk,
   * zodat "medewerker klaar" hier exact hetzelfde betekent als in de portal.
   */
  medewerkerVoortgang: {
    aantalKlaar: number
    aantalTotaal: number
  }
  /** batch-39 (FO-337 §2.4) — aantal actieve-maar-niet-invulbare stappen. */
  aantalNietInvulbaar: number
  // batch-42 onboarding (FO-369) — afgeleide "wacht op"-reden + directe actie
  // (detail-banner). Zelfde bron als de lijst-kolom en de dashboard-telling.
  blokkade: OnboardingBlokkadeDTO
  // FO-369 §6.3 — nudge-snooze (bekende, bewust geparkeerde onboarding). Null =
  // geen snooze actief. `nudgeSnoozeTot` in ISO-datum (UTC); de UI toont 'm in NL.
  nudgeSnoozeTot: string | null
  nudgeSnoozeReden: string | null
  createdAt: string
  updatedAt: string
}

/**
 * batch-39 (FO-337 §2.4) — opschoonlijst: onboardings die "ingediend of
 * verder" zijn maar volgens de éénbron-afleiding nog open of niet-invulbare
 * medewerker-stappen hebben (historisch inconsistente gevallen). HR handelt
 * per geval af: stap alsnog laten invullen, of vervallen verklaren met reden
 * (overslaan met notitie).
 */
export interface OnboardingOpschoonRijDTO {
  id: string
  employeeName: string
  status: OnboardingStatusValue
  ingediendOp: string
  openMedewerkerStappen: Array<{ volgorde: number; titel: string; status: OnboardingStapStatus }>
  nietInvulbareStappen: Array<{ volgorde: number; categorie: OnboardingCategorie | null }>
}

export interface OnboardingStatsDTO {
  concept: number
  inBehandeling: number
  afgerondDezeMaand: number
  gemiddeldeDoorlooptijdDagen: number | null // gemiddelde van laatste 90 dagen, null als geen samples
}

/**
 * F3.7 — uitgebreide dashboard-stats voor `/onboarding`-overzicht in backoffice.
 * `OnboardingStatsDTO` blijft bestaand (gebruikt door legacy-stats card-row);
 * deze DTO voegt KPI's toe voor doorlooptijd-trend, completion-rate en
 * gemiddelde voortgang.
 */
export interface OnboardingDashboardStatsDTO {
  /** Aantal IN_BEHANDELING (inclusief CONCEPT-zonder-activiteit niet meegerekend). */
  actiefAantal: number
  /** Gemiddelde doorlooptijd in dagen over de laatste 90 dagen. `null` zonder samples. */
  gemiddeldeDoorlooptijdDagen: number | null
  /**
   * Completion-rate in procent (0..100): aantal AFGEROND / (AFGEROND + GEANNULEERD)
   * over de laatste 90 dagen. `null` als noemer 0.
   */
  completionRatePercent: number | null
  /**
   * Gemiddeld aantal AFGEROND-stappen per actief proces (rond op 1 decimaal).
   * `null` als geen actieve processen.
   */
  gemiddeldeStappenAfgerond: number | null
}

// ─── F3.2 — Onboarding-templates ─────────────────────────────────────────────

/**
 * Eén stap binnen een template. `formulierSchema` is een serialized array met
 * `FormulierVeldDefinitie[]`-shape uit `formulieren.ts`. Bij het aanmaken van
 * een Onboarding worden deze rijen gebruikt om OnboardingStap-rijen te
 * genereren.
 */
export interface OnboardingTemplateStapDTO {
  id: string
  templateId: string
  volgnummer: number
  categorie: OnboardingCategorie
  titel: string
  beschrijving: string | null
  verplicht: boolean
  /** Serialized `FormulierVeldDefinitie[]` — dynamische veld-definities. */
  formulierSchema: unknown
  wieVult: WieVultStap
  createdAt: string
  updatedAt: string
}

export interface OnboardingTemplateDTO {
  id: string
  naam: string
  beschrijving: string | null
  entityId: string | null
  entityName: string | null
  functieFilter: string | null
  actief: boolean
  isStandaard: boolean
  auteurUserId: string | null
  auteurNaam: string | null
  stappen: OnboardingTemplateStapDTO[]
  createdAt: string
  updatedAt: string
}

export interface OnboardingTemplateListRowDTO {
  id: string
  naam: string
  beschrijving: string | null
  entityId: string | null
  entityName: string | null
  functieFilter: string | null
  actief: boolean
  isStandaard: boolean
  aantalStappen: number
  createdAt: string
  updatedAt: string
}
